import { cn } from "tailwind-variants";
import type {
	ApiBlockGridItemModel,
	ApiBlockGridModel,
} from "#/_generated/umbraco/delivery-api/models";
import {
	Grid,
	GridColumn,
} from "#/_packages/umbrella/integrations/umbraco/components/ui/grid";
import { BlockGridItemRenderer } from "#/_packages/umbrella/integrations/umbraco/utils/blockgrid-item-renderer.tsx";
import { shouldRenderBlockGridItem } from "#/_packages/umbrella/integrations/umbraco/utils/block-visibility";
import { toResponsiveVisibilityCss } from "#/_packages/umbrella/utils/visibility-utils.ts";
import { BlockMediaScope, MediaComposition } from "../utils/media-composition";

type BlockGridProps = {
	contentArea?: ApiBlockGridModel;
	noVerticalSpacing: boolean;
	className?: string;
	style?: React.CSSProperties;
	prioritizeLeadingMedia?: boolean;
};

export function BlockGrid({
	contentArea,
	className,
	style,
	noVerticalSpacing,
	prioritizeLeadingMedia,
}: BlockGridProps) {
	if (!contentArea) return;

	if (noVerticalSpacing)
		return (
			<div className="no-row-gap">
				<MediaComposition
					items={contentArea.items}
					enabled={prioritizeLeadingMedia}
				>
					<BlockGridItems items={contentArea.items} showLevels={true} />
				</MediaComposition>
			</div>
		);

	return (
		<Grid
			columns={12}
			className={cn(className, "_root", "_block-grid", "[1/6]")}
			style={style}
		>
			<MediaComposition
				items={contentArea.items}
				enabled={prioritizeLeadingMedia}
			>
				<BlockGridItems items={contentArea.items} showLevels={true} />
			</MediaComposition>
		</Grid>
	);
}

type BlockGridItemsProps = {
	items?: Array<ApiBlockGridItemModel>;
	showLevels?: boolean;
};

export function BlockGridItems({ items, showLevels }: BlockGridItemsProps) {
	return (
		<>
			{items?.map((item) => {
				if (!shouldRenderBlockGridItem(item)) {
					return null;
				}

				const visibilityClassName = getResponsiveVisibilityClassName(item);

				return (
					<GridColumn
						className={cn(
							showLevels ? "[2/6]" : "",
							getGridItemResponsiveVisibilityClassName(item),
							getGridColumnClassName(item),
							`grid-column-item.content.contentType.${item.content.contentType}`,
						)}
						columnSpan={item.columnSpan}
						key={item.content.id}
					>
						<div className={cn(getBlockGridItemClassName(item))}>
							{visibilityClassName ? (
								<div className={cn(getResponsiveVisibilityClassName(item))}>
									<BlockGridItem
										item={item}
										className={showLevels ? "[3/6]" : ""}
									/>
								</div>
							) : (
								<BlockGridItem
									item={item}
									className={showLevels ? "[3/6]" : ""}
								/>
							)}
						</div>
					</GridColumn>
				);
			})}
		</>
	);
}

interface IBlockGridItem {
	item: ApiBlockGridItemModel;
	className?: string;
}

function BlockGridItem({ item, className }: IBlockGridItem) {
	return (
		<BlockMediaScope columnSpan={item.columnSpan}>
			<BlockGridItemRenderer model={item} className={className} />
		</BlockMediaScope>
	);
}

type BlockGridItemSettings = {
	hideBlock?: boolean | null;
	publishStartDate?: string | null;
	publishEndDate?: string | null;
	margin?: string[] | null;
	responsiveVisibility?: string[] | null;
	alignmentHorizontal?: string | null;
	alignmentSelfVertical?: string | null;
};

function getBlockGridItemSettings(
	item: ApiBlockGridItemModel,
): BlockGridItemSettings | undefined {
	return item.settings?.properties as BlockGridItemSettings | undefined;
}

function getBlockGridItemClassName(
	item: ApiBlockGridItemModel,
	additionalCss?: string,
) {
	return cn(
		"_block-grid-item",
		`_block-grid-item--${item.content?.contentType.toLowerCase()}`,
		additionalCss,
	);
}

function getGridColumnClassName(
	item: ApiBlockGridItemModel,
	additionalCss?: string,
) {
	const settings = getBlockGridItemSettings(item);
	return cn(
		getAlignmentVerticalClassName(settings?.alignmentSelfVertical),
		getDesktopStartClassName(settings?.alignmentHorizontal, item.columnSpan),
		getMarginClassName(settings?.margin),
		additionalCss,
	);
}

function getMarginClassName(margin?: string[] | null) {
	if (!margin?.length) return;

	return cn(
		margin.includes("Start") && "ml-auto",
		margin.includes("Top") && "mt-auto",
		margin.includes("End") && "mr-auto",
		margin.includes("Bottom") && "mb-auto",
	);
}

function getResponsiveVisibilityClassName(item: ApiBlockGridItemModel) {
	// Search owns its visibility so the header and drawer use Razor's breakpoints.
	if (item.content.contentType === "searchFieldUmbBlock") return null;
	const settings = getBlockGridItemSettings(item);
	if (!settings?.responsiveVisibility?.length) return null;

	return toResponsiveVisibilityCss(settings.responsiveVisibility);
}

function getGridItemResponsiveVisibilityClassName(item: ApiBlockGridItemModel) {
	if (item.content.contentType === "searchFieldUmbBlock") return null;
	const settings = getBlockGridItemSettings(item);
	if (!settings?.responsiveVisibility?.length) return null;

	const visible = new Set(settings.responsiveVisibility);
	const visibilityClassNames = {
		xs: { grid: "grid", hidden: "hidden" },
		sm: { grid: "sm:grid", hidden: "sm:hidden" },
		md: { grid: "md:grid", hidden: "md:hidden" },
		lg: { grid: "lg:grid", hidden: "lg:hidden" },
		xl: { grid: "xl:grid", hidden: "xl:hidden" },
	} as const;

	return cn(
		Object.entries(visibilityClassNames).map(([breakpoint, classNames]) =>
			visible.has(breakpoint) ? classNames.grid : classNames.hidden,
		),
	);
}

function getAlignmentVerticalClassName(alignment?: string | null) {
	switch (alignment?.toLowerCase()) {
		case "stretch":
			return "g-align-v-stretch";
		case "start":
			return "g-align-v-start";
		case "center":
			return "g-align-v-center";
		case "end":
			return "g-align-v-end";
		default:
			return;
	}
}

function getDesktopStartClassName(
	alignment?: string | null,
	columnSpan?: number,
) {
	const horizontal = alignment?.toLowerCase();
	if (horizontal !== "center" && horizontal !== "end") return;

	const span = Math.trunc(columnSpan ?? 12);
	const diff = 12 - span;
	if (diff < 0) return;

	const start = horizontal === "center" ? Math.trunc(diff / 2) + 1 : diff + 1;
	const startClassNames: Record<number, string> = {
		1: "lg:col-start-1",
		2: "lg:col-start-2",
		3: "lg:col-start-3",
		4: "lg:col-start-4",
		5: "lg:col-start-5",
		6: "lg:col-start-6",
		7: "lg:col-start-7",
		8: "lg:col-start-8",
		9: "lg:col-start-9",
		10: "lg:col-start-10",
		11: "lg:col-start-11",
		12: "lg:col-start-12",
	};

	return startClassNames[start];
}
